import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { BondsServices } from './bonds.service';
import { SeatsPanelComponent } from '../components/bloques/seats-panel/seats-panel.component';
import { InfoCardComponent } from '../components/shared/info-card/info-card.component';

export interface PassSeat {
  id: number;
  row: any;
  seat: any;
  section: string | null;
}

/**
 * Compra de un abono en butaca única: la misma butaca en todos sus eventos.
 * Lo comparten el asistente de la web y el diálogo de abonos del tótem.
 *
 * - Obligatoria (`discount.single_seat`): no hay flujo evento a evento, se
 *   elige en el mapa del abono con `pickSeats`.
 * - Ofrecida: en el resto de abonos de eventos específicos, cuando la butaca
 *   elegida en el primer evento está libre en todos, `offerSameSeat` propone
 *   llevársela a todos; si el comprador no quiere, sigue evento a evento.
 */
@Injectable({
  providedIn: 'root'
})
export class SingleSeatPassService {

  constructor(private dialog: MatDialog, private bondsService: BondsServices) { }

  /** ¿Se le puede ofrecer la butaca única a este abono? Solo a los de eventos específicos. */
  canOffer(pass: any): boolean {
    return !!pass && !pass.single_seat && !pass.min_events;
  }

  /**
   * Consulta si las butacas elegidas en el mapa de `eventId` están libres en
   * todos los eventos del abono y, si lo están, pregunta al comprador si las
   * quiere en todos. Devuelve el desglose si acepta y null en cualquier otro
   * caso (no están libres, el abono no cumple los requisitos, dice que no o la
   * consulta falla): quien llama sigue entonces con su flujo evento a evento.
   */
  offerSameSeat(pass: any, eventId: number, seatIds: number[]): Observable<any | null> {
    if (!this.canOffer(pass) || seatIds.length === 0) {
      return of(null);
    }

    return this.bondsService.quotePassSeats(pass.id, seatIds, eventId).pipe(
      switchMap((res: any) => {
        const quote = res?.data;
        const events = quote?.seats?.[0]?.lines?.length || 0;
        if (!quote || events < 2) {
          return of(null);
        }

        const many = seatIds.length > 1;
        const total = (+quote.total).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const text = `<strong>${many ? 'Tus butacas están libres' : 'Tu butaca está libre'} en los ${events} eventos del abono.</strong><br>`
          + `¿Quieres ${many ? 'las mismas butacas' : 'la misma butaca'} en todos? El abono te quedaría en ${total} €.`;

        return this.dialog.open(InfoCardComponent, {
          panelClass: 'info-dialog',
          disableClose: true,
          data: { icon: true, text, btnCancel: true, textButton: 'Sí, en todos', textCancel: 'No, gracias' }
        }).afterClosed().pipe(map((accepted: any) => accepted === true ? quote : null));
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Abre el mapa único del abono y devuelve las butacas elegidas (una por
   * abono), o null si se cierra sin completar la selección. Pide el mapa en
   * cada apertura: así, tras un aviso de butaca ocupada, sale ya actualizado.
   * `onOpened` avisa de que el mapa ya está en pantalla (el tótem quita ahí su
   * spinner de carga).
   */
  pickSeats(pass: any, quantity: number, onOpened?: () => void): Observable<PassSeat[] | null> {
    return this.bondsService.getPassSeatMap(pass.id).pipe(
      switchMap((res: any) => {
        const passMap = res.data;

        // Con la venta de uno de sus eventos cerrada el abono no se puede
        // comprar (el back lo rechaza al añadirlo): se avisa antes de dejar
        // elegir butaca, y diciendo qué evento es.
        const closed = (passMap.events || []).find((e: any) => !!e.sale_closed);
        if (closed) {
          return throwError(() => ({
            status: 400,
            error: { message: `Este abono no se puede comprar ahora por «${closed.title}»: ${closed.sale_closed}` }
          }));
        }
        // Para el comprador solo hay butacas libres y no libres: las que no
        // tienen entrada general en algún evento se pintan como ocupadas.
        const mapEvent = {
          ...passMap,
          booked_seats: [...(passMap.booked_seats || []), ...(passMap.unavailable_seats || [])],
        };

        const seatDialog = this.dialog.open(SeatsPanelComponent, {
          panelClass: ['info-dialog', 'not-padding', 'panel-seats'],
          data: {
            // El panel solo usa el evento para etiquetar la selección
            event: { id: passMap.reference.event_id, title: pass.name, company: { id: pass.id_company } },
            session: { id: passMap.reference.session_id },
            mapEvent,
            tickets: [],
            fromAbono: true,
            singleSeatPass: true,
            abono: pass,
            abonoQuantity: quantity,
          }
        });

        onOpened?.();

        return seatDialog.afterClosed().pipe(
          map((data: any) => {
            const seats = data?.seats || [];
            if (seats.length < quantity) {
              return null;
            }
            return seats.map((s: any) => ({ id: s.id, row: s.row, seat: s.seat, section: s.section?.name || null }));
          })
        );
      })
    );
  }
}
